import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Tiny Tim Cost Tile — last 7 days.
 *
 * Reads `/api/tiny-tim/call-log/daily` for per-day buckets and renders a
 * compact sparkline + total. Once Ollama is live on Mac Mini, the bars on
 * the right of the sparkline (most recent days) should drop visibly toward
 * zero as classifier/lookup work moves off Sonnet.
 */
const TinyTimCostTile = ({ days = 7 }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tiny-tim/call-log/daily`, { params: { days } });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load cost data');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (error) {
    return (
      <div
        data-testid="tiny-tim-cost-tile-error"
        className="rounded-xl border border-red-900/40 bg-red-950/20 p-4 text-sm text-red-300"
      >
        Tiny Tim cost tile unavailable: {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        data-testid="tiny-tim-cost-tile-loading"
        className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-500"
      >
        Loading Tiny Tim cost…
      </div>
    );
  }

  const buckets = data.buckets || [];
  const maxCost = Math.max(0.0001, ...buckets.map((b) => b.cost_usd || 0));
  const totalCost = data.total_cost_usd ?? 0;
  const totalCalls = data.total_calls ?? 0;

  return (
    <div
      data-testid="tiny-tim-cost-tile"
      className="rounded-xl border border-slate-800 bg-slate-950/40 p-4"
    >
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Tiny Tim · last {data.days || days} days
        </p>
        <span
          data-testid="tiny-tim-cost-total-calls"
          className="text-[11px] text-slate-500"
        >
          {totalCalls.toLocaleString()} calls
        </span>
      </div>
      <p
        data-testid="tiny-tim-cost-total-usd"
        className="text-2xl font-semibold text-emerald-400 leading-none"
      >
        ${totalCost.toFixed(4)}
      </p>
      <div
        data-testid="tiny-tim-cost-sparkline"
        className="mt-3 flex items-end gap-1 h-10"
        aria-label="daily cost sparkline"
      >
        {buckets.map((b) => {
          const h = Math.max(2, Math.round(((b.cost_usd || 0) / maxCost) * 36));
          const isToday = b.date === new Date().toISOString().slice(0, 10);
          return (
            <div
              key={b.date}
              data-testid={`tt-spark-${b.date}`}
              title={`${b.date}: $${(b.cost_usd || 0).toFixed(4)} • ${b.calls} calls`}
              className={`flex-1 rounded-sm ${
                isToday ? 'bg-emerald-400' : 'bg-emerald-700/70'
              }`}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-slate-600">
        Right bar = today. Bars shrink as Ollama takes over local work.
      </p>
    </div>
  );
};

export default TinyTimCostTile;
